import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import { MyShift } from '../../../../domain/myShift';
import { mergeTimeRanges, timeToMinutes, minutesToTime } from '../../../../domain/myShift/TimeSlotProcessor';

/**
 * DayCellの背景色定義
 */
const DAY_CELL_COLORS = {
  /** 通常（平日） */
  default: '#ffffff',
  /** 週末（土日） */
  weekend: '#fdf8f7',
  /** 祝日 */
  holiday: '#fdf8f7',
  /** 前月/翌月の日付 */
  otherMonth: '#faf9f7',
  /** 過去日付 */
  past: '#faf9f7',
  /** ホバー時 */
  hover: '#faf2f0',
  /** フォーカス時のボーダー */
  focusBorder: '#a6636b',
  /** シフト登録済み */
  hasShift: '#fff8f7',
} as const;

/**
 * DayCellのテキスト色定義
 */
const DAY_CELL_TEXT_COLORS = {
  /** 通常 */
  default: '#302d2a',
  /** 祝日 */
  holiday: '#ae686d',
  /** 非アクティブ（前月/翌月、過去） */
  inactive: '#a39b94',
} as const;

/**
 * カレンダー日付セルのProps
 */
interface DayCellProps {
  /** 表示する日付 */
  date: Date;
  /** 現在表示中の月の日付か（false=前月/翌月のグレーアウト表示） */
  isCurrentMonth: boolean;
  /** 週末（土日）フラグ */
  isWeekend: boolean;
  /** 祝日フラグ */
  isHoliday: boolean;
  /** 過去日付フラグ（true=編集不可） */
  isPast: boolean;
  /** 登録済みシフトデータ */
  shift?: MyShift;
  /** クリックハンドラー */
  onClick: () => void;
}

/**
 * 背景色を決定
 */
const getBackgroundColor = (props: DayCellProps): string => {
  const { isCurrentMonth, isPast, isWeekend, isHoliday, shift } = props;

  if (!isCurrentMonth) return DAY_CELL_COLORS.otherMonth;
  if (isPast) return DAY_CELL_COLORS.past;
  if (shift) return DAY_CELL_COLORS.hasShift;
  if (isHoliday || isWeekend) return DAY_CELL_COLORS.weekend;
  return DAY_CELL_COLORS.default;
};

/**
 * テキスト色を決定
 */
const getTextColor = (props: DayCellProps): string => {
  const { isCurrentMonth, isPast, isHoliday } = props;

  if (!isCurrentMonth || isPast) return DAY_CELL_TEXT_COLORS.inactive;
  if (isHoliday) return DAY_CELL_TEXT_COLORS.holiday;
  return DAY_CELL_TEXT_COLORS.default;
};

/**
 * シフト時間の表示テキストを生成（連続する時間帯はマージして表示）
 */
const getShiftTimeDisplay = (shift?: MyShift): string[] | null => {
  if (!shift || !shift.timeSlots || shift.timeSlots.length === 0) return null;

  // TimeSlotをTimeRangeに変換
  const ranges = shift.timeSlots.map(slot => ({
    startMinutes: timeToMinutes(slot.startTime),
    endMinutes: timeToMinutes(slot.endTime),
  }));

  // 連続する時間帯をマージ
  const merged = mergeTimeRanges(ranges);

  // 表示用文字列に変換
  return merged.map(range =>
    `${minutesToTime(range.startMinutes)}-${minutesToTime(range.endMinutes)}`
  );
};

/**
 * カレンダー日付セルコンポーネント
 */
export const DayCell: React.FC<DayCellProps> = (props) => {
  const { date, isCurrentMonth, isPast, shift, onClick } = props;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  const backgroundColor = getBackgroundColor(props);
  const textColor = getTextColor(props);
  const shiftTimeDisplay = getShiftTimeDisplay(shift);
  const isClickable = isCurrentMonth && !isPast;

  return (
    <Box
      role="button"
      tabIndex={isClickable ? 0 : -1}
      aria-label={`${format(date, 'M月d日')}${shift ? ' シフトあり' : ''}`}
      aria-disabled={!isClickable}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      sx={{
        minWidth: 0,
        minHeight: { xs: 64, sm: 100, md: 116 },
        p: { xs: 0.5, sm: 1 },
        backgroundColor,
        color: textColor,
        cursor: isClickable ? 'pointer' : 'default',
        borderRight: '1px solid #e8e4df',
        borderBottom: '1px solid #e8e4df',
        transition: 'background-color 0.2s, border-color 0.2s',
        '&:hover': isClickable
          ? {
              backgroundColor: DAY_CELL_COLORS.hover,
            }
          : undefined,
        '&:focus': {
          outline: 'none',
          borderColor: DAY_CELL_COLORS.focusBorder,
          borderWidth: 2,
        },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={isCurrentMonth ? 700 : 400}
        sx={{ display: 'grid', placeItems: 'center', width: 23, height: 23, borderRadius: '50%', fontSize: 12 }}
      >
        {format(date, 'd')}
      </Typography>
      {shiftTimeDisplay && shiftTimeDisplay.map((timeText, index) => (
        <Typography
          key={index}
          variant="caption"
          sx={{
            mt: index === 0 ? 0.5 : 0,
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            px: 0.5,
            py: 0.25,
            borderRadius: '4px',
            bgcolor: '#f1f0ed',
            color: '#895e5b',
            fontWeight: 700,
            fontSize: 9,
            lineHeight: 1.3,
            display: { xs: index === 0 ? 'block' : 'none', sm: 'block' },
          }}
        >
          {timeText}
        </Typography>
      ))}
    </Box>
  );
};

export default DayCell;
